import type { Dictionary } from './dictionaryTypes';
import { EN } from './en';
import { RU_UI_KIT } from './ruUiKit';
import { SETTINGS_PHONE_CHANGE_RU } from './settingsPhoneChange';
import { VPN_PLUS_SHARE_CODE_RU } from './vpnPlusShareCode';

export const RU: Dictionary = {
    localeLabel: 'Русский',
    languages: {
        ru: 'Русский',
        en: 'English',
        es: 'Español',
        fr: 'Français',
        ar: 'العربية',
        'zh-CN': '简体中文',
        'zh-HK': '廣東話',
    },
    common: {
        close: 'Закрыть',
        done: 'Готово',
        settings: 'Настройки',
        preferences: 'Предпочтения',
        logout: 'Выйти',
        loading: 'Загрузка…',
        copied: 'Скопировано!',
        backToDashboard: 'К дашборду',
        backToChats: 'К чатам',
        backToRecordings: 'К записям',
        login: 'Войти',
        forInvestors: 'Инвесторам',
        learnMore: 'Подробнее',
        chats: 'Чаты',
        email: 'Почта',
        openCall: 'Открыть звонок',
        currentBrowser: 'Этот браузер',
        anotherDevice: 'Другое устройство',
        thisBrowser: 'Этот браузер',
        moreActions: 'Ещё',
    },
    appError: {
        title: 'Что-то пошло не так',
        description: 'Ошибка зафиксирована. Перезагрузите страницу, чтобы повторить попытку.',
        retry: 'Повторить',
    },
    offline: {
        eyebrow: 'Офлайн',
        title: 'Нет соединения',
        description: 'Можно открыть кэшированные экраны Chattenger, но новые сообщения, звонки, почта и синхронизация требуют сеть.',
        openChats: 'Открыть чаты',
    },
    compatibilityRoutes: {
        errorEyebrow: 'Маршрут ошибки',
        errorTitle: 'Общая поверхность ошибки теперь рендерится через Next.js.',
        errorDescription: 'Так маршрут остаётся доступным, пока остальная поверхность продукта уходит с устаревшего SPA.',
        towerEyebrow: 'Совместимый маршрут',
        towerTitle: 'Tower теперь открывается через оболочку Next.js.',
        towerDescription:
            'Переход к игре останется доступным во время миграции со SPA на Next. Маршрут зарезервирован и больше не зависит от устаревшего бандла.',
        backHome: 'Назад в Chattenger',
        joinEyebrow: 'Переход по ссылке',
        joinInvalidTitle: 'Ссылка-приглашение недействительна',
        joinInvalidDescription: 'Эта ссылка-приглашение повреждена. Откройте её снова в Chattenger или попросите новую ссылку.',
        openInChattenger: 'Открыть в Chattenger',
        continueOnWeb: 'Продолжить в вебе',
        joinDirectChatTitle: 'Не удалось открыть direct-чат',
        joinDirectChatDescription:
            'Пользователь был найден, но браузерный чат сейчас не удалось инициализировать. Попробуйте открыть его в приложении.',
        goToDashboard: 'Перейти к дашборду',
        joinChannelEyebrow: 'Вход в канал',
        joinGroupEyebrow: 'Вход в группу',
        joinUnsupportedChannelDescription: 'Веб-клиент распознал этот канал, но он пока недоступен в вашем текущем списке чатов браузера.',
        joinUnsupportedGroupDescription: 'Веб-клиент распознал эту группу, но она пока недоступна в вашем текущем списке чатов браузера.',
        joinUnsupportedChannelFallback: 'Откройте этот канал в Chattenger, чтобы просмотреть его или завершить доступ либо подписку.',
        joinUnsupportedGroupFallback: 'Откройте эту группу в Chattenger, чтобы просмотреть её или завершить доступ либо подписку.',
        joinNotFoundTitle: 'Приглашение не найдено',
        joinNotFoundDescription:
            'Это приглашение не удалось разрешить для текущего аккаунта. Оно может быть недействительным, приватным или больше недоступно.',
    },
    theme: {
        system: 'Система',
        light: 'Светлая',
        dark: 'Тёмная',
        label: 'Тема',
        hint: 'Тема сохраняется отдельно для этого браузера.',
    },
    settings: {
        eyebrow: 'Профиль и настройки',
        title: 'Предпочтения интерфейса',
        description: 'Язык и тема сохраняются для текущего браузера и остаются вашими при следующем входе.',
        appearanceTab: 'Оформление',
        devicesTab: 'Устройства',
        languageTab: 'Язык',
        securityTab: 'Безопасность',
        appearanceTitle: 'Сценарий оформления',
        appearanceCopy: 'Переключайте палитру без перезагрузки. Веб-клиент должен оставаться быстрым и стабильным на любом устройстве.',
        languageTitle: 'Язык интерфейса',
        languageCopy: 'Локализация меняется мгновенно и применяется ко всем ключевым поверхностям текущего веб-сеанса.',
        securityTitle: 'Доверенный доступ',
        securityCopy:
            'Подтверждайте новые устройства, отзывайте старые браузеры и просматривайте старые инвайты мобильной регистрации из доверенной сессии.',
        profileTitle: 'Текущий профиль',
        profileCopy: 'Настройки для этого пользователя и этого браузера.',
        publicCopy: 'Публичная версия хранит тему и язык на уровне устройства, без отдельного аккаунта.',
        photoAction: 'Фото',
        photoActionAria: 'Выбрать фото профиля',
        photoUploadFailed: 'Не удалось загрузить фото.',
        navigationTitle: 'Разделы',
        languageLabel: 'Язык',
        languageHint: 'Сейчас поддерживаются русский, английский, español, français, العربية, 简体中文 и 廣東話.',
        localeBadge: 'Язык',
        themeBadge: 'Тема',
        accessibilityTitle: 'Доступность',
        accessibilityCopy:
            'Полностью отключайте семантический accessibility-слой для этого браузера, если вам нужен максимально нейтральный визуальный режим без ARIA/role-контрактов.',
        accessibilityLabel: 'Доступность',
        accessibilityOn: 'Вкл',
        accessibilityOff: 'Выкл',
        focusHighlightsTitle: 'Подсветка фокуса',
        focusHighlightsCopy: 'Отключайте визуальные focus-highlight рамки для этого браузера, если они мешают обычной работе мышью или тачем.',
        focusHighlightsLabel: 'Подсветка фокуса',
        focusHighlightsOn: 'Вкл',
        focusHighlightsOff: 'Выкл',
        securityLoadFailed: 'Не удалось загрузить раздел безопасности.',
        securityActionFailed: 'Не удалось выполнить действие безопасности.',
        securityDateUnavailable: 'Недоступно',
        currentDeviceCannotBeRevoked: 'Этот браузер является текущим доверенным устройством и не может отозвать сам себя.',
        ...SETTINGS_PHONE_CHANGE_RU,
        devicesTitle: 'Активные устройства',
        devicesCopy:
            'Смотрите все сессии телефона, браузера и desktop-клиента для этого аккаунта. Онлайн-статус обновляется через авторизованный сокет.',
        devicesLoadFailed: 'Не удалось загрузить устройства.',
        devicesActionFailed: 'Не удалось обновить сессию устройства.',
        devicesEmpty: 'Для этого аккаунта пока не найдено устройств.',
        devicesCurrent: 'Текущее устройство',
        devicesView: 'Посмотреть',
        devicesEndSession: 'Завершить сессию',
        devicesEndingSession: 'Завершаем…',
        devicesLoggedInAt: 'Вход',
        devicesUpdatedAt: 'Обновлено',
        devicesActiveSockets: 'Активные сокеты',
        devicesUserAgent: 'User agent',
        devicesStatusOnline: 'Онлайн',
        devicesStatusOffline: 'Офлайн',
        devicesStatusSignedOut: 'Вышло',
        deviceInspectionBack: 'Назад к устройствам',
        deviceInspectionCopy: 'Посмотрите недавнюю активность этого устройства. Раскройте событие, чтобы увидеть метаданные.',
        deviceInspectionEmpty: 'Недавней активности пока нет.',
        deviceActivityNoMetadata: 'Метаданных нет',
        deviceViewRequest: 'Запросить live-просмотр',
        deviceViewRequestMicrophone: 'Только микрофон',
        deviceViewRequestCameraMic: 'Камера + микрофон',
        deviceViewPermissionSummary: 'Доступ на устройстве: микрофон {microphone}, камера {camera}.',
        deviceViewPermissionGranted: 'разрешён',
        deviceViewPermissionPrompt: 'спросит',
        deviceViewPermissionDenied: 'заблокирован',
        deviceViewPermissionUnknown: 'неизвестно',
        deviceViewPermissionUnsupported: 'не поддерживается',
        deviceViewPermissionHint: 'Выбирайте уже разрешённые опции, чтобы не упереться в системный запрос на том устройстве.',
        deviceViewIdle: 'Активного запроса просмотра нет.',
        deviceViewPending: 'Ждём запуск камеры и микрофона на другом устройстве.',
        deviceViewStarting: 'Запускаем камеру и микрофон.',
        deviceViewStreaming: 'Камера и микрофон транслируются.',
        deviceViewPermissionRequired: 'На том устройстве нужен доступ к камере или микрофону.',
        deviceViewFailed: 'Live-просмотр не запустился.',
        deviceViewStopped: 'Live-просмотр остановлен.',
        deviceViewStreamingChip: '{count} устр. смотрит камеру и микрофон',
        deviceViewAccept: 'Подтвердить',
        deviceViewDecline: 'Отклонить',
        deviceViewPromptTitle: 'Разрешить просмотр устройства?',
        deviceViewPromptBody: 'Другое устройство аккаунта просит просмотреть этот браузер. Подтверждайте только если это запросили вы.',
        deviceViewRespondFailed: 'Не удалось ответить на запрос просмотра устройства.',
        deviceViewOverlayTitle: 'Live-просмотр устройства',
        deviceViewOverlayCopy: 'Здесь отображаются статус просмотра и безопасный журнал действий выбранного устройства.',
        deviceViewOverlayMediaPending: 'Live-поток камеры и микрофона появится здесь, когда другое устройство начнёт трансляцию.',
        deviceViewOverlayAudioActive: 'Аудиопоток активен',
        deviceViewOverlayLogsTitle: 'Поток действий',
        pendingApprovalsTitle: 'Ожидающие подтверждения устройства',
        pendingApprovalsCopy: 'Эти попытки входа ждут подтверждения с одного из ваших доверенных устройств.',
        pendingApprovalsEmpty: 'Сейчас нет ожидающих подтверждений устройств.',
        pendingApprovalsApprove: 'Подтвердить',
        pendingApprovalsReject: 'Отклонить',
        pendingApprovalsKnownDevice: 'Известное устройство',
        pendingApprovalsUnknownDevice: 'Новое устройство',
        trustedDevicesTitle: 'Доверенные устройства',
        trustedDevicesCopy: 'Просматривайте браузеры и клиенты, которые могут входить без повторного одобрения.',
        trustedDevicesEmpty: 'Для этого аккаунта пока не найдено доверенных устройств.',
        trustedDevicesCurrent: 'Текущее устройство',
        trustedDevicesRevoke: 'Отозвать устройство',
        trustedDevicesTrustedAt: 'Доверено',
        trustedDevicesUpdatedAt: 'Обновлено',
        trustedDevicesStatusTrusted: 'Доверено',
        trustedDevicesStatusPending: 'Ожидает',
        trustedDevicesStatusRevoked: 'Отозвано',
        invitesTitle: 'Инвайты мобильной регистрации',
        invitesCopy: 'Просматривайте старые инвайты, привязанные к телефону. Новые аккаунты создаются только в мобильном приложении Chattenger.',
        invitesEmpty: 'Пока нет ни активных, ни исторических инвайтов.',
        invitesRevoke: 'Отозвать инвайт',
        invitesExpiresAt: 'Истекает',
        invitesConsumedAt: 'Использован',
        invitesRevokedAt: 'Отозван',
        invitesStatusActive: 'Активен',
        invitesStatusConsumed: 'Использован',
        invitesStatusRevoked: 'Отозван',
    },
    landing: {
        eyebrow: 'Платформа безопасной коммуникации',
        title: 'Chattenger — безграничные возможности',
        description:
            'Экосистема безопасной коммуникации для союзов, государств, компаний и команд. Суверенные коммуникации, цифровое доверие и путь к более крупной экономической инфраструктуре.',
        searchHint: 'Chattenger. Чаттенджер. Чатенджер.',
        openLogin: 'Открыть вход',
        learnMore: 'Подробнее',
        badges: ['Responsive first', 'Socket native', 'Investor ready'],
        capabilities: [
            'Чаты, группы, каналы, голосовые, circle video и зашифрованные direct-потоки.',
            'Синхронная работа между телефоном и браузером без разрыва пользовательского опыта.',
            'Администрирование, поддержка, записи и рабочие поверхности в одном продукте.',
        ],
        investorsEyebrow: 'BRICS / SCO / B2B / B2C',
        investorsTitle:
            'Chattenger — это коммуникационное ядро, которое можно развивать в сторону суверенных цифровых экосистем, госуслуг, финансов, логистики и отраслевых super-app поверхностей.',
        investorsDescription:
            'Защищённая коммуникация, цифровой суверенитет, расширяемая mini-app архитектура и путь от доверенного мессенджера к более крупной экономической инфраструктуре.',
        coreNow: 'Что уже есть',
        platformDirection: 'Куда платформа идёт',
        coreList: [
            'Чаты, звонки, групповые комнаты, обмен медиа, уведомления и записи.',
            'Приватные сообщения, защищённые звонки и единый пользовательский опыт на всех устройствах.',
            'Администрирование, поддержка и защищённые рабочие поверхности для операторов.',
        ],
        platformList: [
            'Mini-apps, KYC, платежи, логистика, CRM, медиа и госсектор.',
            'Коммуникационная экосистема масштаба альянсов и государств, а не только consumer messenger.',
            'Гибкая платформа для региональных развёртываний и отраслевых контуров.',
        ],
    },
    auth: {
        title: 'Chattenger',
        loginTitle: 'Войдите по коду',
        loginDescription: 'Введите номер для входа. QR тоже доступен.',
        runtimeUnavailableTitle: 'Стенд недоступен',
        runtimeUnavailableDescription: 'Сервер не отвечает. Вход может не сработать, пока стенд восстанавливается.',
        methodLabel: 'Способ входа',
        methodOtp: 'OTP',
        methodQr: 'QR',
        countryLabel: 'Страна',
        phoneLabel: 'Номер телефона',
        phonePlaceholder: '8 (900) 450-65-54',
        continue: 'Продолжить',
        requesting: 'Отправляем…',
        otpLabel: 'OTP-код',
        otpHint: 'Введите код подтверждения.',
        callOtpHint: 'Ответьте на звонок и введите код, который услышите.',
        smsOtpHint: 'Мы сейчас вам отправим СМС. Введите код.',
        verify: 'Подтвердить',
        verifying: 'Проверяем…',
        resendCode: 'Отправить код заново',
        resendIn: 'Повтор через {seconds}с',
        retryCall: 'Повторить звонок',
        retrySms: 'СМС ещё раз',
        switchToCall: 'Попробовать звонок',
        switchToSms: 'Попробовать СМС',
        changePhone: 'Изменить номер',
        requestOtpFailed: 'Не удалось запросить OTP',
        verifyOtpFailed: 'Не удалось подтвердить OTP',
        pendingDeviceApprovalTitle: 'Подтвердите вход',
        pendingDeviceApprovalDescription:
            'Этот аккаунт уже существует. Завершите вход с одного из доверенных устройств, прежде чем браузер получит доступ.',
        pendingDeviceApprovalApproveOnTrustedDevice: 'Откройте Chattenger на доверенном устройстве и подтвердите этот вход.',
        pendingDeviceApprovalCodeLabel: 'Код подтверждения',
        pendingDeviceApprovalQrLabel: 'QR-код подтверждения устройства',
        pendingDeviceApprovalWaiting: 'Ожидаем подтверждение…',
        pendingDeviceApprovalApproved: 'Подтверждено. Завершаем вход…',
        pendingDeviceApprovalExpiresIn: 'Истекает через {seconds}с',
        pendingDeviceApprovalExpiredStatus: 'Истекло',
        pendingDeviceApprovalRejectedStatus: 'Отклонено',
        pendingDeviceApprovalNotFoundStatus: 'Недоступно',
        pendingDeviceApprovalExpired: 'Запрос на подтверждение истёк. Начните вход заново.',
        pendingDeviceApprovalRejected: 'Этот запрос был отклонён на доверенном устройстве.',
        pendingDeviceApprovalNotFound: 'Этот запрос больше недоступен. Начните заново.',
        pendingDeviceApprovalPollFailed: 'Не удалось обновить статус подтверждения.',
        cantLogin: 'Не получается войти?',
        changedNumberRecovery: 'Я сменил номер',
        changedNumberRecoveryTitle: 'Сменили номер?',
        changedNumberRecoveryDescription:
            'Введите старый номер аккаунта, один код восстановления и новый номер. Мы подтвердим новый телефон перед восстановлением.',
        changedNumberRecoveryNewPhoneLabel: 'Новый номер',
        changedNumberRecoveryNewPhonePlaceholder: 'Новый номер',
        changedNumberRecoveryRequestOtp: 'Отправить код на новый номер',
        changedNumberRecoveryComplete: 'Восстановить с новым номером',
        changedNumberRecoveryOtpSent: 'Код отправлен на новый номер.',
        changedNumberRecoveryRequesting: 'Отправляем…',
        changedNumberRecoveryCompleting: 'Восстанавливаем…',
        registrationEmailTitle: 'Подтвердите email',
        registrationEmailDescription: 'Введите код из письма, прежде чем аккаунт будет создан.',
        registrationEmailSentTo: 'Мы отправили 4-значный код на {email}.',
        trustedDeviceBootstrapEmailTitle: 'Подтвердите это доверенное устройство',
        trustedDeviceBootstrapEmailDescription: 'Введите код из письма, чтобы завершить вход на этом устройстве.',
        trustedDeviceBootstrapEmailSentTo: 'Мы отправили 4-значный код на {email}.',
        mobileRegistrationRequired: 'Новые аккаунты создаются только в мобильном приложении Chattenger.',
        emailOtpLabel: 'Код из email',
        emailOtpHint: 'Введите 4-значный код из письма.',
        registrationRecoveryCodesTitle: 'Сохраните коды восстановления',
        accountRecoveryTitle: 'Восстановление аккаунта',
        accountRecoveryDescription: 'Введите код с телефона и один код восстановления, чтобы сбросить доверенные устройства.',
        recoveryCodeLabel: 'Код восстановления',
        recoveryCodePlaceholder: 'CHTG-XXXX-XXXX-XXXX-XXXX',
        recoveryCodeHint: 'Используйте один резервный код, который вы сохранили ранее.',
        accountRecoveryCodesTitle: 'Новые коды восстановления',
        recoveryCodesDescription: 'Эти коды показываются один раз. Храните их приватно и офлайн.',
        recoveryCodesWriteDown: 'Запишите эти коды. Это единственный самостоятельный способ восстановить аккаунт позже.',
        copyRecoveryCodes: 'Скопировать коды',
        emailRecoveryCodes: 'Отправить коды',
        recoveryCodesCopied: 'Коды скопированы',
        recoveryCodesDeliveryTitle: 'Отправка кодов восстановления',
        recoveryCodesDeliveryDescription: 'Отправьте копию на {email} или сохраните непрочитанную копию во входящих Chattenger после входа.',
        recoveryCodesVerifiedEmailLabel: 'подтверждённый email',
        recoveryCodesInboxCopyTitle: 'Создать непрочитанную копию в почте Chattenger',
        recoveryCodesInboxCopyDescription: 'После входа откроется почта, а письмо с кодами останется непрочитанным, пока вы его не выберете.',
        sendRecoveryCodesEmail: 'Отправить на email',
        recoveryCodesEmailSending: 'Отправляем…',
        recoveryCodesInboxCopyCreating: 'Создаём копию во входящих Chattenger…',
        recoveryCodesEmailSent: 'Письмо отправлено',
        recoveryCodesNextCountdown: 'Далее ({seconds})',
        qrEyebrow: 'Chattenger Web',
        qrTitle: 'Вход по QR',
        qrDescription: 'Откройте Chattenger на телефоне и подтвердите вход.',
        qrInstructionOne: 'Откройте Chattenger.',
        qrInstructionTwo: 'Перейдите в настройки.',
        qrInstructionThree: 'Выберите вход в веб и отсканируйте код.',
        qrRefreshing: 'Готовим новый код…',
        qrWaiting: 'Ожидаем подтверждение с телефона…',
        qrExpired: 'Код истёк. Сгенерируйте новый QR.',
        qrFailed: 'Не удалось подготовить QR-вход',
    },
    admin: {
        control: 'Контур',
        role: 'Роль',
        roleCopy: 'Админ-раздел отделён от обычного рабочего пространства пользователя.',
        nav: {
            overview: 'Обзор',
            messages: 'Сообщения',
            statistics: 'Статистика',
            logs: 'Логи',
            infrastructure: 'Инфраструктура',
            wireguardInternal: 'Внутренний WireGuard',
            stands: 'Стенды',
            builds: 'Сборки',
            users: 'Пользователи',
            permissions: 'Права',
            db: 'База данных',
            docs: 'Документация',
            mermaid: 'Редактор Mermaid',
            reports: 'Репорты',
            email: 'Почта',
            mailboxes: 'Почтовые ящики',
            terminal: 'Терминал',
            authConfig: 'Auth-настройки',
            features: 'Фичи',
        },
        surfaceCanonicalContractLabel: 'Канонический контракт',
        docsWebPlanPageTitle: 'Веб-план',
        docsShellEyebrow: 'Админ / Документы',
        docsShellDescription: 'Каноническое описание планирования и реализации текущей веб-поверхности.',
        mermaid: {
            canvasLabel: 'Рабочая область диаграммы',
            editorTab: 'Редактор',
            errorLink: 'Ссылка на диаграмму неверна или превышает безопасный лимит.',
            errorSyntax: 'Диаграмму не удалось отрисовать. Исправьте синтаксис Mermaid и повторите попытку.',
            fit: 'Вписать диаграмму',
            loading: 'Загружаем редактор…',
            pageDescription:
                'Редактируйте приватную диаграмму в браузере и делитесь ограниченным состоянием URL без сохранения исходника в Chattenger.',
            pageEyebrow: 'Админ / Mermaid',
            pageTitle: 'Редактор Mermaid',
            previewTab: 'Просмотр',
            ready: 'Диаграмма готова',
            rendering: 'Отрисовываем диаграмму…',
            reset: 'Сбросить вид',
            share: 'Скопировать ссылку',
            shareCopied: 'Ссылка скопирована',
            shareUnavailable: 'В этом браузере нельзя скопировать ссылку.',
            sourceLabel: 'Исходник Mermaid',
            zoomIn: 'Увеличить',
            zoomOut: 'Уменьшить',
        },
        logsPage: {
            shellEyebrow: 'Админ / Логи',
            shellTitle: 'Логи наблюдаемости',
            shellDescription: 'Запрос операционных логов у провайдеров через Grafana Loki и Rollbar без отката к чтению только с диска.',
            searchLabel: 'Поиск',
            searchPlaceholder: 'Поиск по сообщению, полезной нагрузке или стеку',
            serviceLabel: 'Сервис',
            sourceLabel: 'Источник',
            sinkLabel: 'Приёмник',
            environmentLabel: 'Окружение',
            severityLabel: 'Уровень',
            optionAnyService: 'Любой сервис',
            optionAnySource: 'Любой источник',
            optionAnySink: 'Любой приёмник',
            optionAnyEnvironment: 'Любое окружение',
            optionAnySeverity: 'Любой уровень',
            btnApply: 'Применить',
            btnApplyBusy: 'Обновление…',
            resultsNoData: 'Нет данных логов',
            resultsSummaryOne: '{total} строка · стр. {page}',
            resultsSummaryMany: '{total} строк · стр. {page}',
            filtersHintCopy: 'Фильтры применяются на сервере с коротким кэшем, чтобы частые обновления админки не долбили Loki и Rollbar.',
            btnPrev: 'Назад',
            btnNext: 'Далее',
            tableColTime: 'Время',
            tableColService: 'Сервис',
            tableColSink: 'Приёмник',
            tableColSource: 'Источник',
            tableColEnv: 'Среда',
            tableColSeverity: 'Уровень',
            tableColMessage: 'Сообщение',
            tableColAction: 'Действие',
            btnInspect: 'Открыть',
            emptyTitle: 'Нет строк',
            emptyCopy: 'Подходящие строки наблюдаемости появятся здесь, когда для этой среды настроены читаемые провайдеры Loki и Rollbar.',
            errLoadLogs: 'Не удалось загрузить логи',
            errChangePage: 'Не удалось сменить страницу',
            providerFallbackLabel: 'Grafana Loki',
            providerFallbackMessage: 'Статус провайдера пока недоступен.',
            providerOkCopy: 'Провайдер успешно опрошен для текущего админ-запроса.',
            providerInactiveCopy: 'Провайдер не активен для текущего запроса.',
            sheetTitleFallback: 'Детали лога',
            sheetEyebrowPattern: '{service} / {sink}',
            sheetSummaryLabel: 'Сводка',
            sheetTimeLine: 'Время: {time}',
            sheetEnvironmentLine: 'Окружение: {env}',
            sheetOpenProviderLink: 'Открыть запись у провайдера',
            sheetRawLabel: 'Сырые данные',
            sheetPayloadLabel: 'Полезная нагрузка',
            valueEmDash: '—',
        },
        messagesPage: {
            shellEyebrow: 'Админ / Сообщения',
            shellTitle: 'Диалоги поддержки',
            shellDescription: 'Живой CRM-инбокс для учётной записи поддержки Chattenger.',
            searchLabel: 'Поиск по чатам поддержки',
            searchPlaceholder: 'Имя, ник или телефон',
            btnFilter: 'Фильтр',
            listEmptyTitle: 'Нет чатов поддержки',
            listEmptyCopy: 'Диалоги поддержки появятся здесь, когда пользователи начнут писать в Chattenger.',
            noMessagesYet: 'Пока нет сообщений',
            lockedPill: 'Заблокирован',
            pendingStreakPill: '{count} в ожидании',
            threadEyebrow: 'Тред поддержки',
            requesterMetaLine: '@{handle} · {count} сообщений пользователя в ожидании',
            openInNewTab: 'Открыть в новой вкладке',
            btnUnlockInput: 'Разблокировать ввод',
            btnLockInput: 'Заблокировать ввод',
            staffAttributionLine: '{name} (Chattenger)',
            fallbackStaffDisplayName: 'Chattenger',
            encryptedMessage: 'Зашифрованное сообщение',
            composerPlaceholderCanReply: 'Ответить от имени Chattenger',
            composerPlaceholderReadOnly: 'У этой учётной записи персонала только чтение в CRM для диалогов поддержки.',
            btnReply: 'Ответить',
            emptyDetailTitle: 'Выберите чат поддержки',
            emptyDetailHeading: 'Здесь откроется панель CRM.',
            emptyDetailCopy:
                'Детализация сообщений, обновления в реальном времени, блокировка ввода и атрибуция действующего админа отображаются в этой панели.',
            errLoadThread: 'Не удалось загрузить тред поддержки',
            errSearchConversations: 'Не удалось выполнить поиск по диалогам поддержки',
            errOpenConversation: 'Не удалось открыть этот диалог',
            errSendReply: 'Не удалось отправить ответ поддержки',
            errUpdateLock: 'Не удалось обновить блокировку чата поддержки',
        },
        reportsPage: {
            shellEyebrow: 'Админ / Отчёты',
            shellTitle: 'Отчёты о продукте',
            shellDescription: 'Входящие отчёты с фильтрами, детализацией и инструментами проверки оператором.',
            sheetEyebrowReportDetails: 'Детали отчёта',
            sheetTitleFallbackReport: 'Отчёт',
            searchLabel: 'Поиск',
            searchPlaceholder: 'Поиск по заголовку, STR, ER, AR',
            userIdLabel: 'ID пользователя',
            userIdPlaceholder: 'ID автора',
            statusLabel: 'Статус',
            statusOptionAny: 'Любой статус',
            mediaLabel: 'Медиа',
            mediaOptionAny: 'Любые медиа',
            mediaOptionVideo: 'Есть видео',
            mediaOptionImages: 'Есть изображения',
            btnApply: 'Применить',
            btnPrev: 'Назад',
            btnNext: 'Далее',
            resultsNoData: 'Нет данных',
            resultsSummaryOne: '{total} отчёт · стр. {page}/{totalPages}',
            resultsSummaryMany: '{total} отчётов · стр. {page}/{totalPages}',
            tableColCreated: 'Создан',
            tableColStatus: 'Статус',
            tableColTitle: 'Заголовок',
            tableColUser: 'Пользователь',
            tableColMedia: 'Медиа',
            tableColAction: 'Действие',
            mediaVideoTag: 'видео',
            valueEmDash: '—',
            mediaImageCountOne: '{count} изображение',
            mediaImageCountMany: '{count} изображений',
            btnInspect: 'Открыть',
            emptyTitle: 'Нет отчётов',
            emptyCopy: 'Подходящие отчёты отладочного бота появятся здесь после создания.',
            errLoadReports: 'Не удалось загрузить отчёты',
            errChangePage: 'Не удалось сменить страницу',
            sheetUserLine: 'Пользователь {userId}',
            sheetStrLabel: 'STR',
            sheetExpectedResultLabel: 'Ожидаемый результат',
            sheetActualResultLabel: 'Фактический результат',
            sheetArtifactsLabel: 'Артефакты',
            sheetVideoKeyLine: 'Ключ видео: {value}',
            sheetImageKeysLine: 'Ключи изображений: {value}',
        },
        standaloneShellEyebrow: 'Админ',
        buildLastRunLabel: 'последний запуск',
        buildEtaInProgressLabel: 'в процессе',
        buildEtaLowConfidencePattern: '{phase} · низкая точность',
        buildEtaNativeCompileLabel: 'нативная сборка',
        buildHistoryArchiveLabel: 'Архив сборок',
        buildHistoryTitle: 'История сборок',
        buildHistoryDescription: 'Завершённые задания хранятся в Postgres; полные логи по-прежнему читаются из файлов билдера по запросу.',
        buildHistoryLoadingLabel: 'Загружаем историю...',
        buildHistoryCountPattern: 'Найдено сборок: {count}',
        buildHistoryStatusLabel: 'Статус',
        buildHistorySearchLabel: 'Поиск',
        buildHistorySearchPlaceholder: 'ветка, job, marker',
        buildHistoryApplyLabel: 'Применить',
        buildHistoryEmptyCopy: 'Подходящей истории сборок пока нет.',
        buildHistoryPageStatusPattern: 'Страница {page} из {pageCount} · сборок: {total}',
        buildHistoryCreatedPrefix: 'создано',
        buildHistoryUploadedLabel: 'загружено',
        buildHistoryAriaLabel: 'История админских сборок',
        buildHistoryColumns: {
            artifact: 'Артефакт',
            branch: 'Ветка',
            duration: 'Длительность',
            finished: 'Завершено',
            job: 'Job',
            requestedBy: 'Запустил',
            status: 'Статус',
            track: 'Контур',
        },
        buildHistoryStatusOptions: {
            all: 'Все',
            cancelled: 'Отменено',
            failed: 'Ошибка',
            interrupted: 'Прервано',
            queued: 'В очереди',
            running: 'Идёт',
            succeeded: 'Успешно',
            terminal: 'Завершённые',
        },
        overviewPage: {
            shellTitle: 'Операционный центр',
            shellDescription:
                'Сообщения, права, пользователи, отчёты, журналы и инструменты персонала для защищённого операционного центра Chattenger.',
            heroCardLabel: 'Обзор',
            heroCardTitle: 'Защищённые инструменты для операторов, поддержки и модерации.',
            heroCardCopy: 'Живой обзор сообщений, пользователей, прав и операционных очередей в одном месте.',
            ctaOpenMessages: 'Открыть сообщения',
            ctaOpenStatistics: 'Открыть статистику',
            ctaOpenLogs: 'Открыть журналы',
            ctaOpenDb: 'Открыть БД',
        },
        docsBrowserPage: {
            shellTitle: 'Рабочие документы',
            shellDescription: 'Канонические планы, сид-доки и справочники реализации для операторов, QA и продуктовых ревью.',
            listPaneLabel: 'Документы',
            listPaneTitle: 'Канонические источники',
            listPaneCopy: 'Используйте эти материалы для матриц поведения QA, планирования выкатов и актуального продуктового контракта.',
            btnSyncSeedDocs: 'Синхронизировать сид-доки с БД',
            btnSyncSeedDocsBusy: 'Синхронизация сид-доков…',
            searchLabel: 'Поиск по документам',
            searchPlaceholder: 'Поиск по заголовку, слагу или описанию',
            detailPaneLabel: 'Выбрано',
            btnCopyRawMarkdown: 'Копировать сырой Markdown',
            copySuccessMessage: 'Сырой Markdown скопирован.',
            copyClipboardUnavailable: 'В этом браузере недоступен буфер обмена.',
            copyFailedFallback: 'Не удалось скопировать сырой Markdown.',
            errSyncSeedDocsFallback: 'Не удалось синхронизировать сид-доки.',
        },
        dbPage: {
            ...EN.admin.dbPage,
            shellEyebrow: 'Админ / БД',
            shellTitle: 'Комната управления PostgreSQL',
            labelActiveDatabase: 'Активная база данных',
            shellDescription:
                'Просмотр живых баз, схем и таблиц, выполнение SQL и создание ролей PostgreSQL через защищённую админ-поверхность бэкенда.',
        },
        usersPage: {
            shellEyebrow: 'Админ / Пользователи',
            shellTitle: 'Глобальное управление ролями',
            shellDescription: 'Глобальные роли отделены от обычного владения чат-админкой. Здесь только common/admin/super_admin.',
            searchLabel: 'Поиск',
            searchPlaceholder: 'Поиск по имени или нику',
            btnSearch: 'Найти',
            tableColUser: 'Пользователь',
            tableColHandle: 'Ник',
            tableColRole: 'Роль',
            roleOptionCommon: 'Обычный',
            roleOptionAdmin: 'Админ',
            roleOptionSuperAdmin: 'Супер-админ',
            emptyTitle: 'Нет подходящих пользователей',
            emptyCopy: 'Управляемые по роли пользователи появятся здесь, когда запрос даст совпадения.',
            errSearchUsers: 'Не удалось выполнить поиск пользователей',
            errUpdateRole: 'Не удалось обновить роль пользователя',
        },
        statsPage: {
            shellEyebrow: 'Админ / Статистика',
            shellTitle: 'CRM-активность и атрибуция',
            shellDescription: 'Фильтры, состояние в URL и атрибуция действующего админа для поддержки и операций с ролями.',
            searchLabel: 'Поиск',
            searchPlaceholder: 'Поиск по актору, цели, пути, тексту',
            eventLabel: 'Событие',
            eventOptionAll: 'Все события',
            btnApply: 'Применить',
            pageStatusPattern: 'Страница {page} из {pageCount} · строк: {total}',
            paginationNext: 'Далее',
            paginationPrevious: 'Назад',
            tableColTime: 'Время',
            tableColEvent: 'Событие',
            tableColActor: 'Актор',
            tableColTarget: 'Цель',
            tableColPath: 'Путь',
            tableColMessage: 'Сообщение',
            actorSystemLabel: 'system',
            valueEmDash: '—',
            emptyTitle: 'Нет строк',
            emptyCopy: 'Строки CRM-активности появятся здесь, когда привилегированные пользователи работают с поддержкой и админ-инструментами.',
            errLoadStats: 'Не удалось загрузить статистику CRM',
        },
        permissionsPage: {
            shellEyebrow: 'Админ / Права',
            shellTitle: 'Матрица прав CRM',
            shellDescription: 'Первая расширяемая поверхность прав CRM. Управляет доступом админов к поддержке и CRM-статистике.',
            tableColPermission: 'Право',
            tableColRole: 'Роль',
            tableColAccessLevel: 'Уровень доступа',
            rowPermissionChatWithUsers: 'Общение с пользователями',
            accessLevelFull: 'Полный доступ',
            accessLevelRead: 'Только чтение',
            accessLevelNone: 'Нет доступа',
            roleUser: 'Пользователь',
            roleAdmin: 'Админ',
            roleSuperAdmin: 'Супер-админ',
            errUpdatePermission: 'Не удалось обновить это право',
        },
        authConfigPage: {
            shellEyebrow: 'Админ / Auth',
            shellTitle: 'Маршрутизация phone auth',
            shellDescription:
                'Postgres-настройка стран. Exolve оставляем для RU/BY, пока это работает; Firebase phone включаем для конкретных международных стран только после проверки billing, SMS region policy и client config. Env остаётся только bootstrap fallback.',
            sourceBootstrapEnv: 'Bootstrap fallback из env',
            sourceDatabase: 'Postgres config',
            defaultSummary: 'Страна по умолчанию: {countryId}',
            searchLabel: 'Поиск стран',
            searchPlaceholder: 'Страна, ISO-код или телефонный код',
            btnSave: 'Сохранить маршруты',
            btnSaving: 'Сохраняем...',
            btnReset: 'Сбросить',
            countriesPageStatusPattern: 'Страница {page} из {pageCount} · стран: {total}',
            errorsPageStatusPattern: 'Страница {page} из {pageCount} · ошибок: {total}',
            paginationNext: 'Далее',
            paginationPrevious: 'Назад',
            tableColCountry: 'Страна',
            tableColProvider: 'Провайдер',
            tableColStatus: 'Статус',
            tableColDefault: 'По умолчанию',
            tableColUpdated: 'Обновлено',
            tableColErrorReason: 'Причина',
            providerExolve: 'Exolve звонок/SMS',
            providerFirebase: 'Firebase phone',
            providerUnsupported: 'Не поддерживается',
            statusEnabled: 'Включена',
            statusDisabled: 'Отключена',
            defaultCurrent: 'Default',
            defaultSet: 'Назначить',
            dirtyCopy: 'Несохранённые изменения остаются только в этом браузере до сохранения маршрутов.',
            defaultInvalid: 'Перед сохранением выберите включённую страну с Exolve или Firebase как default.',
            emptyTitle: 'Страны не найдены',
            emptyCopy: 'По текущему поиску нет стран.',
            errorsTitle: 'Недавние ошибки phone auth провайдеров',
            errorsEmptyTitle: 'Недавних ошибок нет',
            errorsEmptyCopy: 'Здесь появятся provider mismatch, отключённые страны, ошибки Firebase и доставки Exolve.',
            saveSuccess: 'Маршрутизация phone auth сохранена.',
            errLoad: 'Не удалось загрузить маршрутизацию phone auth.',
            errSave: 'Не удалось сохранить маршрутизацию phone auth.',
            valueEmDash: '—',
        },
        featuresPage: {
            shellEyebrow: 'Админ / Фичи',
            shellTitle: 'Флаги функций',
            shellDescription:
                'Локальные для окружения флаги функций из backend control plane. Используйте только для осознанного rollout; секреты провайдеров и raw env остаются на сервере.',
            sourceDatabase: 'Postgres feature config',
            sourceCopy: 'Текущий хост автоматически соответствует своему runtime-окружению.',
            searchLabel: 'Поиск фич',
            searchPlaceholder: 'Название фичи',
            btnEnable: 'Включить',
            btnDisable: 'Отключить',
            btnSave: 'Сохранить',
            btnSaving: 'Сохраняем...',
            btnReset: 'Сбросить',
            tableColFeature: 'Фича',
            tableColStatus: 'Статус',
            tableColScope: 'Область',
            tableColUpdated: 'Обновлено',
            tableColAction: 'Действие',
            statusEnabled: 'Включена',
            statusDisabled: 'Отключена',
            scopeServerSide: 'Server-side',
            scopeClientVisible: 'Видима клиенту',
            scopeUnknown: 'Неизвестно',
            dirtyCopy: 'Несохранённые изменения фич остаются только в этом браузере до сохранения.',
            emptyTitle: 'Фичи не найдены',
            emptyCopy: 'По текущему поиску нет фич.',
            saveSuccessPattern: '{featureName} сохранена.',
            errLoad: 'Не удалось загрузить флаги функций.',
            errSave: 'Не удалось сохранить этот флаг функции.',
            valueEmDash: '—',
        },
        mailboxesPage: {
            shellEyebrow: 'Админ / Почтовые ящики',
            shellTitle: 'Учётные записи и алиасы',
            shellDescription: 'Плоскость супер-админа для реальных логинов почты, алиасов, назначений и локального владения консолью.',
            runtimeSectionLabel: 'Рантайм',
            runtimeHostSyncEnabledTitle: 'Включена локальная синхронизация почтового хоста с окружением',
            runtimeHostSyncDisabledTitle: 'Синхронизация почтового хоста отключена',
            runtimeMessageFallback: 'Информация о рантайме почты недоступна.',
            createFormEyebrow: 'Создать учётную запись почты',
            createFormTitle: 'Новая учётная запись',
            btnCreateMailbox: 'Создать ящик',
            fieldSlug: 'Слаг',
            fieldDisplayName: 'Отображаемое имя',
            fieldLoginUser: 'Логин',
            fieldPassword: 'Пароль',
            fieldPasswordPlaceholderNewMailbox: 'Обязателен для нового активного ящика',
            fieldPasswordHintKeepCurrent: 'Оставьте пустым, чтобы сохранить текущий пароль.',
            fieldFromAddress: 'Адрес From',
            fieldFromName: 'Имя From',
            fieldReplyTo: 'Reply-To',
            fieldConsoleBinding: 'Привязка к консоли',
            consoleBindingNone: 'Без привязки к консоли',
            consoleBindingEmail: '/admin/mail',
            fieldStatus: 'Статус',
            statusPendingSetup: 'Ожидает настройки',
            statusActive: 'Активен',
            statusDisabled: 'Отключён',
            statusArchived: 'В архиве',
            fieldSentMailbox: 'Папка «Отправленные»',
            fieldTrashMailbox: 'Папка «Корзина»',
            fieldThreadScanLimit: 'Лимит сканирования тредов',
            fieldInboxPageSize: 'Размер страницы входящих',
            aliasesSectionLabel: 'Алиасы',
            btnAddAlias: 'Добавить алиас',
            btnRemoveAlias: 'Удалить алиас',
            aliasFieldLabelPattern: 'Алиас {index}',
            checkboxPrimaryAlias: 'Основной алиас',
            checkboxEnabled: 'Включён',
            assignedUsersLabel: 'Назначенные пользователи Chattenger',
            assignedUsersHintCreate: 'Обычные пользователи — не более одного ящика. Админы и супер-админы могут иметь несколько.',
            assignedUsersHintEdit: 'Обычным пользователям доступен один ящик. Админам и супер-админам — несколько.',
            userOptionRoleLine: 'Роль: {role}',
            forceAliasReassignCreate: 'Принудительно переназначить алиас, если он уже привязан к другому ящику',
            forceConsoleKeyReassignCreate: 'Принудительно перенести привязку консоли, если она уже занята',
            forceAliasReassignEdit: 'Принудительно переназначить алиас',
            forceConsoleKeyReassignEdit: 'Принудительно перенести привязку консоли',
            accountCardEyebrowPattern: 'Учётная запись почты №{id}',
            accountLoginUserLabel: 'Логин:',
            accountSystemMailboxSuffix: ' • системный ящик',
            btnSave: 'Сохранить',
            btnArchive: 'В архив',
            archiveBlockedConsoleHint: 'Снимите привязку консоли с этого ящика перед архивацией.',
            archiveBlockedSystemHint: 'Системные ящики зарезервированы и не архивируются здесь.',
            errCreateFallback: 'Не удалось создать учётную запись почты',
            errSaveFallback: 'Не удалось сохранить учётную запись почты',
            errArchiveFallback: 'Не удалось отправить учётную запись в архив',
            errConflictAliasLine: '{value} уже привязан к {mailboxDisplayName}.',
            errConflictConsoleKeyLine: '{value} уже привязан к {mailboxDisplayName} как консоль.',
            errConflictLoginLine: 'Логин {value} уже используется в {mailboxDisplayName}.',
            createSuccessLine: 'Ящик {displayName} создан.',
            saveSuccessLine: 'Ящик {displayName} сохранён.',
        },
        ciPage: {
            shellTitle: 'CI',
            shellDescription:
                'Выберите окружение, закрепите ветку и задеплойте Next.js в стабильные корни рантайма без повторного использования изменяемого рабочего чекаута.',
        },
        wireguardPage: {
            shellTitle: 'Внутренний WireGuard',
            shellDescription:
                'Просмотр внутренних control-plane WireGuard-пиров с внешнего хоста, недавние рукопожатия и отключение скомпрометированных операторских пиров без выхода из админки.',
            disablePeerDialogTitle: 'Отключить пира',
            summaryPeersLabel: 'Пиры',
            summaryMetaLine: '{active} активных · {inactive} устаревших · {never} без подключения',
            summaryInterfaceLabel: 'Интерфейс',
            summaryGeneratedLine: 'Снимок на {time}',
            transportPolicyLabel: 'Граница транспортов',
            transportPolicyWireguardCopy: 'Эта страница управляет только legacy WireGuard-пирами для intranet/control-plane.',
            transportPolicyAmneziaCopy:
                'Пользовательские regular VPN-хосты AmneziaWG и VPN+ VLESS/H1Cloud — отдельные поверхности до transport-aware live-контролов в backend/dev-tools.',
            transportPolicyMediaWorkerCopy: 'Media worker для /transcribe, /describe и Suno не управляется на этой странице.',
            snapshotEyebrow: 'Живой снимок',
            snapshotPollCopy: 'Опрос каждые {seconds} с, пока вкладка видна. Отключение идёт напрямую на внешний хост.',
            peerCountBadge: '{count} пиров',
            badgeNoData: 'Нет данных',
            btnRefresh: 'Обновить',
            btnRefreshBusy: 'Обновление…',
            errLoadPeers: 'Не удалось загрузить список пиров WireGuard.',
            tableColPeer: 'Пир',
            tableColStatus: 'Статус',
            tableColAllowedIps: 'Разрешённые IP',
            tableColHandshake: 'Рукопожатие',
            tableColTraffic: 'Трафик',
            tableColEndpoint: 'Конечная точка',
            tableColAction: 'Действие',
            peerUnlabeled: 'Пир без метки',
            peerNoIdComment: 'Нет комментария peer id',
            trafficRxPrefix: 'RX',
            trafficTxPrefix: 'TX',
            btnDisable: 'Отключить',
            emptyTitle: 'Нет пиров',
            emptyCopy: 'WireGuard не вернул настроенных пиров для этого интерфейса, или внешний хост недоступен.',
            sheetDisableWarning: 'Пир будет немедленно удалён с внешнего интерфейса WireGuard.',
            sheetPeerLabel: 'Пир',
            sheetStatusLabel: 'Статус',
            btnCancel: 'Отмена',
            btnDisablePeer: 'Отключить пира',
            btnDisablePeerBusy: 'Отключение…',
            disablePeerSuccessLine: 'Пир {display} отключён.',
            errDisablePeer: 'Не удалось отключить пира.',
            valueEmDash: '—',
        },
        terminalPage: {
            shellEyebrow: 'Админ / Терминал',
            shellTitle: 'Codex-терминал',
            shellDescription:
                'Запускайте и смотрите локальные сессии Codex из админки. Поверхность ограничена Codex и не открывает произвольный shell.',
            statusReady: 'Codex готов',
            statusMissingHome: 'Codex home не найден',
            statusMissingBinary: 'Бинарь Codex не найден',
            sessionStatusRunning: 'Работает',
            sessionStatusExited: 'Завершена',
            sessionStatusFailed: 'Ошибка',
            knownSessionCached: 'В кэше',
            knownSessionMissing: 'Не найдена',
            environmentLabel: 'Окружение',
            prefixLabel: 'Префикс сессии',
            homePathLabel: 'Codex home',
            binaryPathLabel: 'Бинарь Codex',
            searchedPathsLabel: 'Проверенные пути',
            activeSessionsLabel: 'Активные сессии',
            knownSessionsLabel: 'Известные Codex-сессии',
            knownSessionSearchEmpty: 'Codex-сессий по этому фильтру нет.',
            knownSessionSearchLabel: 'Фильтр известных Codex-сессий',
            knownSessionSearchPlaceholder: 'Фильтр сессий...',
            noSessionsTitle: 'Сессий Codex из браузера пока нет.',
            noSessionsCopy: 'Создайте сессию, чтобы писать Codex прямо из этой админ-поверхности.',
            noKnownSessionsTitle: 'В кэше пока нет Codex-сессий.',
            noKnownSessionsCopy: 'Обновите индекс после того, как Codex использовался на этом хосте.',
            newSessionTitle: 'Новая Codex-сессия',
            promptLabel: 'Первый prompt',
            promptPlaceholder: 'Попросите Codex выполнить задачу на этом хосте...',
            btnNewSession: 'Новая сессия',
            btnNewSessionBusy: 'Запускаем...',
            btnRefresh: 'Обновить',
            btnStop: 'Остановить',
            btnSend: 'Отправить',
            btnSendBusy: 'Отправляем...',
            inputLabel: 'Сообщение Codex',
            inputPlaceholder: 'Введите prompt, команду или инструкцию...',
            outputTitle: 'Вывод терминала',
            historyTitle: 'История Codex',
            emptyOutput: 'Вывод появится здесь, когда Codex начнет отвечать.',
            selectSessionTitle: 'Codex-сессия не выбрана.',
            selectSessionCopy: 'Откройте известную сессию, чтобы загрузить проверенную историю, или начните новую live-сессию.',
            historyMissing: 'Эта Codex-сессия осталась в кэше, но её session log больше не найден на диске.',
            historyLoading: 'Загружаем историю Codex...',
            historyLoadingMore: 'Загружаем более раннюю историю Codex...',
            unavailableTitle: 'Этот хост не готов для админских Codex-сессий.',
            errLoad: 'Не удалось обновить состояние терминала.',
            errCreate: 'Не удалось запустить Codex-сессию.',
            errSend: 'Не удалось отправить ввод в Codex.',
            errStop: 'Не удалось остановить Codex-сессию.',
            valueEmpty: 'Недоступно',
        },
        placeholderPage: {
            migrationCardLabel: 'Миграция в процессе',
        },
        mailConsole: {
            shellEyebrow: 'Админ / Почта',
            shellTitle: 'Служебный ящик поддержки',
            shellDescription: 'Служебный и операционный ящик для входящих писем клиентов и внутренних процессов.',
            inboxPaneEyebrow: 'Входящие',
            inboxTitle: 'Почта поддержки',
            inboxDescription: 'Работа с операционной почтой без выхода из админского контура Chattenger.',
            composeLabel: 'Новое письмо',
            composePlaceholder: 'Напишите письмо простым текстом',
            composeReplyHeading: 'Черновик ответа',
            composeForwardHeading: 'Черновик пересылки',
            emptyInboxCopy: 'Входящие сейчас пусты.',
            emptySelectionCopy: 'Выберите строку во входящих, чтобы открыть письмо, или начните новый черновик.',
            successPrefix: 'Письмо принято к отправке',
            actionNewEmail: 'Новое письмо',
            actionReply: 'Ответить',
            actionForward: 'Переслать',
            actionDelete: 'Удалить',
            sendEmail: 'Отправить письмо',
            addAttachments: 'Добавить вложения',
            refreshInbox: 'Обновить входящие',
            fieldTo: 'Кому',
            fieldCc: 'Копия',
            fieldBcc: 'Скрытая копия',
            fieldSubject: 'Тема',
            fieldBody: 'Текст',
            placeholderTo: 'user@example.com',
            placeholderCc: 'cc@example.com',
            placeholderBcc: 'bcc@example.com',
            placeholderSubject: 'Тема',
            attachmentsBadge: 'Вложения',
            attachmentRemoveAffix: ' • убрать',
            previewUnavailable: 'Превью пока нет',
            selectedMessageEyebrow: 'Выбранное письмо',
            selectedMessageEmptyTitle: 'Письмо не выбрано',
            unknownDate: 'Неизвестная дата',
            fromLabel: 'От',
            toLabel: 'Кому',
            ccLabel: 'Копия',
            messageIdLabel: 'Message-ID',
            deliveryLabel: 'Доставка',
            htmlOnlyFallback: 'Письмо содержит только HTML. Исходный HTML здесь по умолчанию скрыт.',
            threadLabel: 'Тред',
            threadUnknownSubject: '(без темы)',
            threadDirectionSent: 'Отправлено',
            threadDirectionReceived: 'Получено',
            errLoadMessage: 'Не удалось загрузить письмо',
            errLoadAttachments: 'Не удалось загрузить вложения',
            errRefreshInbox: 'Не удалось обновить входящие',
            errOpenMessage: 'Не удалось открыть это письмо',
            errDeleteMessage: 'Не удалось удалить это письмо',
            errSendEmail: 'Не удалось отправить письмо',
            successMovedToPattern: 'Письмо перемещено в {destination}',
            deliveryQueued: 'В очереди',
            deliveryDeferred: 'Отложено',
            deliverySent: 'Отправлено',
            deliveryBounced: 'Отказ',
            deliveryExpired: 'Истекло',
            deliveryUnknown: 'Неизвестно',
        },
        infrastructure: {
            shellTitle: 'Инфраструктура',
            shellEyebrow: 'Админ',
            shellDescription: 'Управление выкладками, состоянием хоста и удалёнными зависимостями текущего общего контура Chattenger.',
            surfaceTabsLabel: 'Поверхность инфраструктуры',
            tabRollout: 'Выкладка',
            tabHosts: 'Хосты',
            rolloutEyebrow: 'Выкладка',
            rolloutTitle: 'Операции выкладки Next.js и веток',
            rolloutCopy:
                'Здесь собраны выкладки Next.js и управление ветками: только запушенные Git refs, стабильные deploy roots по окружениям и реальные удалённые зависимости без маскировки под локальные PM2.',
            operatorBoundaryEyebrow: 'Граница runtime',
            operatorBoundaryTitle: 'Ответственность VPN и media',
            operatorBoundaryCopy:
                'Эта страница показывает runtime-зависимости. Она не создаёт VPN-пиров, не bootstrap-ит AmneziaWG-хосты, не шейпит квоты и не переносит media-нагрузку.',
            operatorBoundaryInternalWireguardTitle: 'Internal WireGuard',
            operatorBoundaryInternalWireguardCopy:
                'Plain WireGuard остаётся intranet/control-plane путём для операторов и не должен показываться как пользовательский regular VPN.',
            operatorBoundaryRegularVpnTitle: 'Regular VPN: AmneziaWG',
            operatorBoundaryRegularVpnCopy:
                'Пользовательский regular VPN относится к AmneziaWG на pc_vpn-хостах после live proof в backend/dev-tools; эта UI-поверхность только обозначает границу.',
            operatorBoundaryVpnPlusTitle: 'VPN+: VLESS/H1Cloud',
            operatorBoundaryVpnPlusCopy: 'VPN+ остаётся отдельным VLESS/H1Cloud-продуктовым путём и не управляется страницей Internal WireGuard.',
            operatorBoundaryMediaWorkerTitle: 'Media worker',
            operatorBoundaryMediaWorkerCopy:
                '/transcribe, /describe и Suno — будущая маршрутизация на pc_worker; эта страница не должна обещать live-контролы миграции.',
            operatorBoundaryNoLiveControlBadge: 'Без live-контроля',
            hostControlsEyebrow: 'Управление хостом',
            httpsNginxTitle: 'HTTPS и операции nginx',
            httpsNginxMeta:
                'Обновите снимок состояния, проверьте nginx, перезагрузите nginx, обновите сертификаты или перезапустите конкретный процесс Next.js.',
            dependencyEyebrowLocal: 'Локальный хост',
            dependencyEyebrowWorkers: 'Backend workers',
            dependencyEyebrowRemote: 'Удалённая зависимость',
            dependencySummaryHealthy: 'в порядке',
            dependencySummaryAttention: 'нужно внимание',
            badgeLive: 'Онлайн',
            badgeUnavailable: 'Недоступно',
            confirmationEyebrow: 'Защита',
            confirmationTitle: 'Живые действия с инфраструктурой требуют явного подтверждения оператора',
            confirmationCopy:
                'Перезагрузки и продление сертификатов сразу затрагивают общий runtime. Сначала вооружите действие, затем введите точную фразу подтверждения.',
            confirmationBadgeGuarded: 'Под защитой',
            confirmationBadgeArmed: 'Вооружено',
            confirmationPendingAction: 'Действие «{action}» для «{scope}» вооружено.',
            confirmationPhraseCopy: 'Введите точную фразу ниже, чтобы выполнить живое действие.',
            confirmationPhraseLabel: 'Точная фраза подтверждения',
            confirmationCancel: 'Отмена',
            confirmationConfirm: 'Выполнить живое действие',
            confirmationRunning: 'Выполнение…',
            confirmationActionRenewCertificate: 'Обновить сертификат',
            confirmationActionReloadNginx: 'Перезагрузить nginx',
            confirmationActionReloadApp: 'Перезапустить приложение',
            actionRefreshSnapshot: 'Обновить снимок',
            actionRefreshingSnapshot: 'Обновление…',
            actionTestNginx: 'Проверить конфиг nginx',
            actionTestingNginx: 'Проверка…',
            actionReloadNginx: 'Перезагрузить nginx',
            actionReloadingNginx: 'Перезагрузка…',
            nginxEyebrow: 'nginx',
            nginxCardTitle: 'Состояние веб-прокси',
            labelServiceState: 'Состояние сервиса',
            labelConfigTest: 'Проверка конфигурации',
            resultPassing: 'успешно',
            resultFailing: 'ошибка',
            stateUnknown: 'неизвестно',
            certbotEyebrow: 'Certbot',
            certbotCardTitle: 'Таймер продления',
            labelNextRun: 'Следующий запуск',
            labelLastRun: 'Последний запуск',
            valueUnknown: 'Неизвестно',
            sharedWith: 'Общий сертификат с',
            httpsBadgeOk: 'HTTPS в порядке',
            httpsBadgeAttention: 'Требуется внимание',
            labelHealthProbe: 'Проверка доступности',
            labelCertificate: 'Сертификат',
            labelPm2Lane: 'Процесс PM2',
            labelUptime: 'Аптайм',
            labelOnlineWorkers: 'Активные workers',
            labelSummary: 'Сводка',
            btnRenewCert: 'Обновить сертификат',
            btnRenewSharedCert: 'Обновить общий сертификат',
            btnRenewing: 'Обновление…',
            btnDryRunRenew: 'Пробный прогон продления',
            btnDryRunShared: 'Пробный прогон (общий)',
            btnDryRunTesting: 'Проверка…',
            btnReloadApp: 'Перезапустить приложение',
            btnReloadingApp: 'Перезапуск…',
            certsEyebrow: 'Сертификаты',
            certsLineagesTitle: 'Установленные цепочки',
            thName: 'Имя',
            thDomains: 'Домены',
            thExpiry: 'Истекает',
            thStatus: 'Статус',
            outputEyebrow: 'Вывод последнего действия',
            outputTitle: 'Журнал команд',
            errRefreshFailed: 'Не удалось обновить состояние инфраструктуры.',
            errActionFailed: 'Действие инфраструктуры не выполнено.',
            actionSuccessLine: '{action} успешно завершено.',
            missingCertificate: 'Отсутствует',
            certExpired: 'Истёк',
            certInvalid: 'Недействителен',
            certValid: 'Действителен',
            certDaysLeft: 'осталось {days} дн.',
            durationUnknown: 'Неизвестно',
            durationDaysHours: '{days} д {hours} ч',
            durationHoursMinutes: '{hours} ч {minutes} мин',
            durationMinutes: '{minutes} мин',
            backendWorkersEyebrow: 'Workers',
            backendWorkersTitle: 'Backend runtime workers',
            backendWorkersSummary: '{online}/{total} онлайн',
            backendWorkersEmpty: 'На этом хосте не найдены backend worker-процессы.',
            remoteHostsEyebrow: 'Удалённый хост',
            remoteHostReachable: 'доступен',
            remoteHostUnreachable: 'недоступен',
            envPreview: 'preview',
            envPreview2: 'preview-2',
            envDevelopment: 'development',
            envDevelopment2: 'development-2',
            envProduction: 'production',
            totalCertificatesBadge: 'всего {count}',
        },
    },
    assistant: {
        navLabel: 'Ассистент',
        eyebrow: 'Ассистент',
        title: 'Спросить ассистента Chattenger',
        description: 'Отдельная поверхность для ассистента. Он не открывается как обычный чат и не показывается в поиске контактов.',
        inputLabel: 'Запрос',
        inputPlaceholder: 'Спросите про Chattenger, рабочее пространство или понятную веб-задачу.',
        submit: 'Спросить',
        submitting: 'Думаю…',
        pendingLabel: 'Ассистент печатает…',
        responseLabel: 'Ответ',
        unavailableTitle: 'Ассистент недоступен',
        unavailableCopy: 'Ассистент сейчас недоступен. Попробуйте позже.',
        emptyCopy: 'Ответы ассистента зависят от включенного backend-провайдера.',
    },
    vpnPlusShareCode: VPN_PLUS_SHARE_CODE_RU,
    dashboard: {
        eyebrow: 'Дашборд',
        title: 'Быстро, синхронно и готово на любом экране.',
        recordings: 'Записи',
        searchChats: 'Поиск по чатам',
        searchChatsPlaceholder: 'Искать чаты',
        searchOlderChats: 'Ищем более старые чаты…',
        globalSearchLabel: 'Искать в Chattenger',
        globalSearchTrigger: 'Поиск',
        globalSearchPlaceholder: 'Поиск чатов, людей и каналов',
        searchBack: 'Назад',
        searchEmptyChats: 'В чатах ничего не найдено.',
        searchEmptyChannels: 'Каналы не найдены.',
        searchFilterAll: 'Все',
        searchFilterChannels: 'Каналы',
        searchFilters: 'Фильтры поиска',
        noChatsCompact: 'чатов пока нет:(',
        quickCreate: 'Быстрое создание',
        create: 'Создать',
        createChat: 'Новый чат',
        createChannel: 'Новый канал',
        createGroup: 'Новая группа',
        closeSearch: 'Закрыть поиск',
        closeQuickCreate: 'Закрыть быстрое меню',
        searchUsers: 'Поиск пользователей',
        searchUsersPlaceholder: 'Найти пользователя',
        searchUsersSearching: 'Ищем пользователей…',
        searchUsersEmpty: 'Пользователи не найдены',
        quickPeople: 'Быстрые контакты',
        notifications: 'Уведомления',
        notificationsCopy: 'Разрешите уведомления, чтобы не пропускать сообщения и звонки в браузере.',
        allowNotifications: 'Разрешить уведомления',
        notificationReady: 'Уведомления готовы',
        refreshPush: 'Обновить push',
        disablePush: 'Отключить push',
        realtimeLabel: 'Связь',
        realtimeOnline: 'На связи',
        realtimeConnecting: 'Подключаемся…',
        realtimeReconnecting: 'Восстанавливаем связь…',
        realtimeOnlineCopy: 'Сообщения и события приходят сразу.',
        realtimeConnectingCopy: 'Интерфейс уже доступен. Соединение сейчас поднимется.',
        realtimeReconnectingCopy: 'Очередь отправки сохранится и продолжит работу после переподключения.',
        syncLabel: 'Синхронизация',
        syncReady: 'Готово',
        syncRefreshing: 'Обновляем…',
        syncing: 'Синхронизируем…',
        saving: 'Сохраняем…',
        hidePreview: 'Скрывать текст в превью уведомлений',
        showPreview: 'Показывать превью',
        vpn: 'VPN',
        vpnChecking: 'Проверяем статус VPN…',
        channel: 'Канал',
        conversation: 'Диалог',
        attachments: 'Вложения',
        searchInChat: 'Поиск внутри чата',
        searchInChatPlaceholder: 'Искать сообщения',
        searchingMessages: 'Ищем…',
        noHits: 'Нет совпадений',
        jumpToLatest: 'К последним',
        queuedSuffix: 'в очереди',
        shellCacheCopy: 'Недавние чаты и отправка сохраняются локально, поэтому интерфейс возвращается сразу.',
        waitingThreadEyebrow: 'Нет выбранного чата',
        waitingThreadTitle: 'Выберите диалог слева.',
        waitingThreadCopy: 'История, поиск и очередь отправки поднимутся сразу после выбора чата.',

        homeRecentLabel: 'Продолжить переписку',

        noChatsEyebrow: 'Нет чатов',
        noChatsCopy: 'Список чатов появится здесь, когда синхронизация завершится.',
        encryptedMessage: 'Зашифрованное сообщение',
        encryptedThreadEmpty: 'Все сообщения зашифрованы',
        conversationPeerOnline: 'В сети',
        conversationPeerLastSeenRecently: 'Недавно в сети',
        conversationPeerLastOnlineAtTime: 'В сети в {time}',
        conversationPeerLastOnlineDateTime: 'В сети {date} в {time}',
        messageMediaQueuedImage: 'Изображение в очереди',
        messageMediaQueuedVideo: 'Видео в очереди',
        messageMediaVideoBadge: 'Видео',
        messageMediaImageFallback: 'Изображение {index}',
        messageMediaVideoFallback: 'Видео {index}',
        messageMediaThumbnailFallback: 'Превью',
        circleVideoExpandAria: 'Развернуть circle video',
        circleVideoResumeAria: 'Продолжить circle video',
        circleVideoCollapseAria: 'Свернуть circle video',
        circleVideoPlaceholder: 'Превью circle video загружается',
        circleVideoPaused: 'На паузе',
        circleVideoLivePreview: 'Живое превью',
        circleVideoUnmute: 'Включить звук',
        circleVideoMute: 'Выключить звук',
        circleVideoSeekHandleAria: 'Перемотать circle video',
    },
    calls: {
        groupCall: 'Групповой звонок',
        directCall: 'Личный звонок',
        waitingMediaEyebrow: 'Ожидание медиа',
        waitingMediaTitle: 'Комната уже живая.',
        waitingMediaCopy: 'Включите микрофон, камеру или шаринг экрана, чтобы начать поток.',
        roomRecordings: 'Записи комнаты',
        recordingsSheetPrivateEyebrow: 'Только для вас',
        viewRecordings: 'Открыть записи',
        callHistory: 'Звонки',
        callHistoryTitle: 'Звонки, записи и живые активности',
        callHistoryCopy: 'Недавние комнаты, личные записи и активности звонков собраны в одном компактном рабочем месте.',
        callHistoryEmpty: 'Звонки появятся здесь после старта или входа в комнату.',
        callHistoryRefresh: 'Обновить',
        callHistoryRefreshing: 'Обновление…',
        callHistoryRecordings: 'Записи',
        callHistoryActivities: 'Активности',
        activities: 'Активности',
        activitiesMenu: 'Активности звонка',
        scrumPoker: 'Scrum poker',
        retroBoard: 'Ретро-доска',
        startScrumPoker: 'Начать poker',
        startRetroBoard: 'Начать ретро',
        activityLoadFailed: 'Не удалось загрузить активности звонка.',
        activityCreateFailed: 'Не удалось создать активность.',
        activityUpdateFailed: 'Не удалось обновить активность.',
        noActivities: 'В этой комнате пока нет активностей.',
        revealCards: 'Открыть карты',
        cardsRevealed: 'Карты открыты',
        pokerVotePrompt: 'Выберите карту',
        pokerWaitingReveal: 'Голоса скрыты, пока ведущий не откроет карты.',
        pokerAverage: 'Среднее {value}',
        retroBoardAddNote: 'Стикер',
        retroBoardAddText: 'Текст',
        retroBoardAddRectangle: 'Прямоугольник',
        retroBoardAddEllipse: 'Эллипс',
        retroBoardSave: 'Сохранить доску',
        retroBoardViewOnly: 'Только просмотр',
        retroBoardMoveHint: 'Тяните доску для панорамирования, масштабируйте колесом или кнопками и перетаскивайте объекты ретро.',
        retroBoardZoomIn: 'Приблизить',
        retroBoardZoomOut: 'Отдалить',
        retroBoardResetView: 'Сбросить вид',
        incomingEyebrow: 'Входящий звонок',
        incomingCopy: 'Ожидает ответа в этом браузере',
        incomingBusyCopy: 'Сначала завершите текущий звонок',
        incomingBusyLabel: 'Активен другой звонок',
        answer: 'Ответить',
        decline: 'Отклонить',
        leave: 'Покинуть',
        microphoneLabel: 'Микрофон',
        videoLabel: 'Камера',
        microphoneOn: 'Микрофон включён',
        microphoneOff: 'Микрофон выключен',
        cameraOn: 'Камера включена',
        cameraOff: 'Камера выключена',
        shareScreen: 'Поделиться экраном',
        stopShare: 'Остановить показ',
        hideSelf: 'Скрыть себя',
        showSelf: 'Показать себя',
        audioDevices: 'Аудиоустройства',
        videoDevices: 'Видеоисточники',
        microphoneDevice: 'Микрофон',
        participantVolume: 'Громкость участника',
        speakerDevice: 'Динамик',
        cameraDevice: 'Камера',
        backgroundEffects: 'Фон',
        backgroundOff: 'Без фона',
        backgroundBlur: 'Размытие',
        backgroundLogo: 'Pigeon',
        backgroundUnavailable: 'Фоны доступны только в поддерживаемых браузерах на достаточно мощных устройствах.',
        backgroundUsesSelectedOutput: 'Фон применяется к тому видеопотоку, который увидят участники и запись звонка.',
        sameAsSystem: 'Как в системе',
        audioProcessing: 'Обработка звука',
        micGain: 'Громкость микрофона',
        micGainHint: 'Применяется к живому микрофону и к записи звонка.',
        noiseSuppression: 'Шумоподавление',
        echoCancellation: 'Подавление эха',
        autoGainControl: 'Автоподстройка громкости',
        audioOutputUnavailable: 'Выбор динамика в этом браузере недоступен. Будет использоваться системный вывод.',
        deviceChangesApplyCopy: 'Смена микрофона, динамика и камеры применяется сразу, без переподключения к комнате.',
        recordingUsesSelectedDevices:
            'Текущая запись разговора использует выбранные микрофон, камеру, громкость микрофона и настройки обработки звука. Вывод на динамик влияет только на воспроизведение на этом устройстве.',
        callEndedUnexpectedly: 'Звонок неожиданно завершился.',
        callParticipantUnavailable: 'Подключить звонок не удалось: сейчас никто не доступен.',
        callConnectionUnavailable: 'Звонки временно недоступны. Попробуйте ещё раз через минуту.',
        callParticipantBusy: 'Собеседник уже находится в другом звонке.',
        callAnsweredElsewhere: 'На этот звонок ответили на другом устройстве.',
        layoutPreviewEyebrow: 'Предпросмотр макета',
        layoutPreviewCopy: 'Этот предпросмотр остаётся локальным в текущем браузере. Реальный звонок и запись не запускаются.',
        switchCamera: 'Сменить камеру',
        callMenu: 'Меню звонка',
        devicesAndRecording: 'Устройства и запись',
        videoChatParticipants: 'Участники видеозвонка',
        screenSharingMenu: 'Демонстрация экрана',
        participantsOnCall: 'В звонке: {count}',
        screenShareActive: 'Экран транслируется',
        screenSharingParticipantLabel: 'Показывает экран: {name}',
        screenShareEmpty: 'Сейчас никто не показывает экран.',
        backToMenu: 'Назад',
        fullscreenPreview: 'Полный экран',
        ongoingCallLabel: 'Идёт звонок',
        ongoingCallParticipants: '{count} в звонке',
        expand: 'Развернуть',
        minimizeCall: 'Свернуть звонок',
        backToAutomaticLayout: 'Вернуться к авто-раскладке',
        liveNow: 'В эфире',
        mutedLabel: 'Без звука',
        recordingStart: 'Начать запись',
        recordingPause: 'Пауза записи',
        recordingResume: 'Продолжить запись',
        screenShareUnsupported: 'Шаринг экрана недоступен в этом браузере.',
        screenSharePermissionHint: 'Разрешите запись экрана, чтобы делиться экраном или вкладкой.',
        screenShareCanceled: 'Шаринг экрана был отменён.',
        screenShareStartFailed: 'Не удалось запустить шаринг экрана.',
        compactActionMore: 'Ещё',
        compactActionMicrophone: 'Мик',
        compactActionCamera: 'Камера',
        compactActionRecord: 'Запись',
        compactActionPause: 'Пауза',
        compactActionResume: 'Продолжить',
        compactActionSwitchCamera: 'Смена',
        compactActionLeave: 'Выйти',
        directPreviewSwap: 'Поменять местами',
        directPreviewHide: 'Скрыть превью',
        directPreviewRestore: 'Вернуть превью',
    },
    assetViewer: {
        dialogLabel: 'Просмотр вложений',
        thumbnailsLabel: 'Элементы',
        open: 'Открыть',
        download: 'Скачать',
        downloadCurrent: 'Только этот файл',
        downloadAll: 'Все файлы',
        downloadChoiceTitle: 'Что скачать?',
        downloadChoiceDescription: 'Сохраните только текущий файл или запустите скачивание всех вложений в этом сообщении.',
        previous: 'Предыдущее',
        next: 'Следующее',
        videoBadge: 'Видео',
        pdfBadge: 'PDF',
        fileBadge: 'Файл',
    },
    mail: {
        attachments: 'Вложения',
        files: 'Файлы',
        shellTitle: 'Почтовый ящик',
        workspaceNavigationLabel: 'Навигация рабочей области',
        workspaceTitle: 'Почтовый ящик',
        mailboxLabel: 'Почтовый ящик',
        actionNewEmail: 'Новое письмо',
        actionRefreshMailbox: 'Обновить',
        actionDeleteMailbox: 'Удалить ящик',
        threadsLabel: 'Почта',
        searchThreadsLabel: 'Поиск по почте',
        filterAllThreads: 'Все',
        filterIncomingThreads: 'Входящие',
        filterAttachmentThreads: 'Вложения',
        inboxQuietCopy: 'Письма появятся здесь, когда этот ящик получит или отправит письмо.',
        inboxQuietEyebrow: 'Пока нет переписок',
        inboxQuietSearchCopy: 'Сейчас нет писем, подходящих под этот поиск.',
        inboxQuietTitle: 'Во входящих тихо',
        incoming: 'Входящие',
        noMailboxDefaultCopy: 'Ваш ящик по умолчанию будет',
        noMailboxEyebrow: 'Почта',
        noMailboxSidebarCopy: 'Создайте первый ящик, чтобы отправлять и получать письма.',
        noMailboxSidebarTitle: 'Ящика пока нет',
        noMailboxTitle: 'У вас пока нет почтового ящика',
        sent: 'Отправленные',
        productDraftNavEyebrow: 'Несохранённый черновик',
        productDraftNavTitle: 'Покинуть черновик?',
        productDraftNavCopy: 'Черновик сохранён локально, но при переходе вы покинете текущий экран написания письма.',
        productDraftNavStay: 'Остаться',
        productDraftNavLeave: 'Покинуть черновик',
        productMailboxDeleteEyebrow: 'Жизненный цикл ящика',
        productMailboxDeleteTitle: 'Удалить ящик?',
        productMailboxDeleteCopyGeneric: 'Удалить этот ящик из обычного интерфейса продукта. Позже его можно восстановить.',
        productMailboxDeleteCopyWithAddress: 'Удалить {address} из обычного интерфейса продукта. Позже ящик можно восстановить из пустого состояния.',
        productMailboxDeleteKeep: 'Оставить ящик',
        productMailboxDeleteConfirm: 'Удалить ящик',
        productMailboxDeleteBusy: 'Удаление…',
        composeLabel: 'Черновик',
        composeReplyHeading: 'Ответ',
        composeForwardHeading: 'Пересылка',
        composeSendingAsPattern: 'Отправка как {address}',
        actionCancel: 'Отмена',
        messageLabel: 'Сообщение',
        threadLabel: 'Тред',
        noSubject: '(без темы)',
        threadMessageCountOne: '{count} сообщение',
        threadMessageCountMany: '{count} сообщений',
        actionReply: 'Ответить',
        actionForward: 'Переслать',
        actionDeleteMessage: 'Удалить',
        unknownSender: 'Неизвестный отправитель',
        unknownDate: 'Неизвестная дата',
        fromLabel: 'От',
        toLabel: 'Кому',
        ccLabel: 'Копия',
        dateLabel: 'Дата',
        deliveryLabel: 'Доставка',
        emptyThreadTitle: 'Выберите письмо слева',
        emptyThreadCopy: 'Откройте переписку или начните новое исходящее письмо.',
        errRefreshMailbox: 'Не удалось обновить этот ящик',
        errOpenMailbox: 'Не удалось открыть этот ящик',
        errOpenThread: 'Не удалось открыть это письмо',
        actionCreateMailbox: 'Создать ящик',
        actionRestoreMailbox: 'Восстановить ящик',
        successMailboxCreated: 'Ящик создан',
        errCreateMailbox: 'Не удалось создать этот ящик',
        successMailboxRestored: 'Ящик восстановлен',
        errRestoreMailbox: 'Не удалось восстановить этот ящик',
        successMailboxDeleted: 'Ящик удалён',
        errDeleteMailbox: 'Не удалось удалить этот ящик',
        errStageAttachments: 'Не удалось подготовить вложения',
        successEmailAccepted: 'Письмо принято к отправке',
        errSendEmail: 'Не удалось отправить это письмо',
        successMessageMovedToTrash: 'Письмо перемещено в корзину',
        errDeleteMessage: 'Не удалось удалить это письмо',
        composerAddAttachments: 'Вложения',
        composerSend: 'Отправить',
        composerSending: 'Отправка…',
        composerHint: 'Перетащите файлы сюда, вставьте скриншоты или нажмите Cmd/Ctrl+Enter для отправки.',
        composerRemoveAttachmentAria: 'Убрать {fileName}',
    },
    recordings: {
        eyebrow: 'Записи',
        title: 'Ваши owner-private сессии записей комнаты по всем устройствам.',
        listTitle: 'Записи',
        noRecordingsEyebrow: 'Записей пока нет',
        noRecordingsTitle: 'Ваши сессии записи появятся здесь.',
        noRecordingsCopy: 'Начните запись группового звонка на вебе или на телефоне, и каждая личная сессия записи появится здесь отдельно.',
        detailEyebrow: 'Детали записи',
        playback: 'Воспроизведение',
        downloadMp4: 'Скачать MP4',
        sceneManifest: 'Scene manifest',
        newBadge: 'Новое',
        playbackPending: 'Эта запись ещё ждёт завершения звонка или финальной упаковки перед воспроизведением.',
        playbackFailed: 'Эта запись не прошла подготовку артефакта.',
        previewLabel: 'Превью',
        roomLabel: 'Комната #{roomId}',
        chatFallbackLabel: 'Чат {chatId}',
        createdLabel: 'Создано',
        roomStateLabel: 'Состояние комнаты',
        roomStateEnded: 'Звонок завершён',
        roomStateRecordingLive: 'Запись идёт',
        roomStateNoActive: 'Нет активной записи',
        roomActivityLabel: 'Активность комнаты',
        roomActivityCount: 'Активных сессий: {count}',
        roomActivityIdle: 'Простой',
        artifactLabel: 'Артефакт',
        durationLabel: 'Длительность',
        durationSeconds: '{seconds} сек',
        playbackReadyLabel: 'Готово к открытию',
        playbackPendingLabel: 'Пока не готово',
        details: 'Детали',
        searchRecordingsLabel: 'Поиск по записям',
        filterAll: 'Все',
        filterReady: 'Готовые',
        filterActive: 'Активные',
        sortRecent: 'Недавние',
        sortChat: 'Чат',
        sortStatus: 'Статус',
        paginationPrevious: 'Назад',
        paginationNext: 'Далее',
        paginationStatus: 'Страница {current} из {total}',
        lastStartedLabel: 'Последний старт',
        pausedAtLabel: 'Пауза',
        pendingRoomEndLabel: 'Ожидает завершения комнаты',
        sealedAtLabel: 'Запечатано',
        artifactReadyLabel: 'Артефакт готов',
        artifactFailedLabel: 'Сбой артефакта',
        sessionIdLabel: 'Идентификатор сессии',
        ownerDeviceLabel: 'Устройство владельца',
        ownerOtherDeviceLabel: 'Другое устройство #{deviceId}',
        artifactStatusLabel: 'Статус артефакта',
        artifactDurationLabel: 'Длительность артефакта',
        artifactSizeLabel: 'Размер артефакта',
        sizeMegabytes: '{size} МБ',
        artifactErrorLabel: 'Ошибка артефакта',
        recordedIntervalsLabel: 'Записанные интервалы',
        timeRangePattern: '{start} → {end}',
        ownerPrivateRecordingTitle: 'Owner-private запись',
        copiedLabel: 'Скопировано',
        copyFailedLabel: 'Ошибка копирования',
        copyDetailsLabel: 'Скопировать детали',
        playPlaybackLabel: 'Пуск',
        pausePlaybackLabel: 'Пауза',
        seekPlaybackLabel: 'Перемотка записи',
        rewindTenSecondsLabel: 'Назад на 10 секунд',
        forwardTenSecondsLabel: 'Вперёд на 10 секунд',
        enterFullscreenLabel: 'На весь экран',
        exitFullscreenLabel: 'Свернуть экран',
        processingTimelineLabel: 'Таймлайн обработки',
        summaryRecordingLine: 'Запись #{id}',
        summaryRoomLine: 'Комната #{roomId}',
        summaryStatusLine: 'Статус: {status}',
        summaryArtifactLine: 'Артефакт: {artifact}',
    },
    uiKit: RU_UI_KIT,
};
